/**
 * The tasks conducted at one business on one day, sortable on every column.
 *
 * Opens ordered by queue time so it reads as a log of the day, and carries its own
 * summary line: the day is fixed by whoever navigated here, so the counts and the
 * active status filter need stating on the page itself.
 */

import React, { useMemo } from 'react';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { formatTimePT } from '../../utils/timezone';
import {
  instant,
  statusLabel,
  type BusinessSummary,
  type DailyTaskRow,
  type StatusFilter,
} from '../../api/robotDailyTasks';
import './DailyTasks.css';

function duration(ms: number | null): string {
  if (ms == null) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

function taskSortValue(row: DailyTaskRow, key: string): unknown {
  switch (key) {
    case 'taskConducted':
      return row.taskConducted;
    case 'status':
      return row.status;
    case 'queuedAt':
    case 'startedAt':
    case 'endedAt':
      return instant(row[key]);
    case 'durationMs':
      return row.durationMs;
    default:
      return null;
  }
}

const BusinessTaskTable: React.FC<{ business: BusinessSummary; status: StatusFilter }> = ({
  business,
  status,
}) => {
  const sort = useTableSortState('queuedAt');
  const tasks = useMemo(
    () => sortRows(business.tasks, sort.sortKey, sort.sortDir, taskSortValue),
    [business.tasks, sort.sortKey, sort.sortDir]
  );

  return (
    <>
      <div className="daily-tasks-meta">
        <p className="daily-tasks-summary">
          {business.taskCount} {business.taskCount === 1 ? 'task' : 'tasks'}
          {status ? ` · ${statusLabel(status)}` : ' · every status'}
          {' · '}
          {formatTimePT(business.firstQueuedAt)} to {formatTimePT(business.lastQueuedAt)}
        </p>
        <span className="daily-tasks-pills">
          {business.statusCounts.map(({ status: s, count }) => (
            <span key={s} className={`daily-tasks-status daily-tasks-status--${s}`}>
              {count} {statusLabel(s)}
            </span>
          ))}
        </span>
      </div>

      <div className="daily-tasks-table-wrapper">
        <table className="daily-tasks-table daily-tasks-table--tasks">
          <thead>
            <tr>
              <SortableTh label="Task Conducted" columnKey="taskConducted" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Status" columnKey="status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Queued" columnKey="queuedAt" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Started" columnKey="startedAt" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Ended" columnKey="endedAt" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Duration" columnKey="durationMs" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.id}>
                <td>{task.taskConducted}</td>
                <td>
                  <span className={`daily-tasks-status daily-tasks-status--${task.status}`}>
                    {statusLabel(task.status)}
                  </span>
                </td>
                <td>{formatTimePT(task.queuedAt)}</td>
                <td>{formatTimePT(task.startedAt)}</td>
                <td>{formatTimePT(task.endedAt)}</td>
                <td>{duration(task.durationMs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default BusinessTaskTable;
